import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ConsentPermission,
  ConsentPermissionRequest,
  ConsentPermissionResponse,
} from './consent.interface';

@Injectable({ providedIn: 'root' })
export class ConsentPermissionAdapter {
  fromResource$(
    resource: ConsentPermissionResponse,
  ): Observable<ConsentPermission> {
    const {
      id,
      description,
      validFrom,
      validTill,
      providerIds,
      facilities,
      childPolicies,
      documentUniqueIds,
      resourceCreationPeriod,
    } = resource;

    let entityIds = [];

    if (documentUniqueIds?.length) {
      entityIds = documentUniqueIds;
    } else if (resource?.resources?.length) {
      entityIds = resource.resources.flatMap(
        ({ resourceIds = [] }) => resourceIds,
      );
    }

    const permission: ConsentPermission = {
      id,
      description,
      validFrom,
      validTill,
      providerId: providerIds ? providerIds[0] : null,
      facilityId: facilities ? facilities[0] : null,
      policyId: childPolicies[0].policyId || null,
      contextLabels: childPolicies[0].contextLabels || null,
      entityIds: entityIds || null,
      resourceCreationPeriod: resourceCreationPeriod || null,
    };

    return of(permission);
  }

  toResource(
    permission: ConsentPermission,
    patientId: string,
    patientRootId: string,
    contextLabel?,
    hasTemplateName?,
  ): ConsentPermissionRequest {
    const { providerId, facilityId, policyId, entityIds, validFrom, entities } =
      permission;

    let providerIds,
      facilityIds,
      templateName,
      policyIds,
      description = null,
      documentUniqueIds = null,
      resourceCreationPeriod = null;
    const resources = [];

    if (providerId && providerId.includes('@')) {
      providerIds = [providerId];
    } else {
      facilityIds = [providerId];
    }

    if (contextLabel == 'readDocument') {
      // todo cockpit translation
      description =
        'Hiermit willige ich in die Dateifreigabe ein. /n Nur die freigegebenen Dateien können angesehen werden.';

      if (hasTemplateName) {
        templateName = 'Einwilligung zum Dokumentenaustausch';
      }

      documentUniqueIds = entityIds;
    } else if (contextLabel == 'readDiaryEntry') {
      description =
        'Ich willige in die Datenfreigabe von Tagebucheinträgen ein. /n Nur die aktuell freigegebenen Tagebucheinträge können angesehen werden.';
      if (hasTemplateName) {
        templateName = 'Einwilligung zur Freigabe von Tagebucheinträgen';
      }
      for (const entity of entityIds) {
        resources.push({ resourceIds: entity, resourceType: 'Observation' });
      }
    } else if (contextLabel == 'readQuestionnaire') {
      description =
        'Ich willige in die Datenfreigabe von Fragebögen ein. /n Nur die aktuell freigegebenen Fragebögen können angesehen werden.';
      if (hasTemplateName) {
        templateName = 'Einwilligung zur Freigabe von Fragebögen';
      }

      if (entities?.length) {
        for (const entity of entities) {
          resources.push({
            resourceIds: entity.id,
            resourceType: entity.resourceType,
          });
        }
      }
    } else if (contextLabel == 'readLogbook') {
      resources.push({
        resourceType: 'AuditEvent',
      });
      description =
        'Ich willige in die Datenfreigabe von Logbucheinträgen ein. /n Nur die aktuell freigegebenen Logbucheinträge können angesehen werden.';
      if (hasTemplateName) {
        templateName = 'Einwilligung zur Freigabe des Logbuchs';
      }

      if (validFrom) {
        resourceCreationPeriod = {
          periodStart: validFrom,
        };
      }
    }

    if (!hasTemplateName) {
      policyIds = [policyId];
    }

    if (facilityId) {
      facilityIds = [facilityId];
    }

    const patientIdWithExtras = patientRootId + '@' + patientId;

    const resource = {
      patientId: patientIdWithExtras,
      policyIds,
      facilityIds,
      providerIds,
      description,
      documentUniqueIds,
      resources,
      templateName,
      resourceCreationPeriod,
      duration: 547,
    };

    return resource;
  }
}
